import React, { useEffect, useState } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import { AppFrame } from 'transfix-ui/components/AppFrame'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Box, Grid, Typography } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { BrokerCarrierAgreementForm } from 'apps/fleet-planner/components/activation/BrokerCarrierAgreementForm'
import { DeprecatedBrokerCarrierAgreementForm } from 'apps/fleet-planner/components/activation/DeprecatedBrokerCarrierAgreementForm'
import { DeprecatedDocumentsUpload } from 'apps/fleet-planner/components/activation/DeprecatedDocumentsUpload'
import { DocumentsUpload } from 'apps/fleet-planner/components/activation/DocumentsUpload'
import { ProvideInsurance } from 'apps/fleet-planner/components/activation/ProvideInsurance'
import { ProvideInsurancePreFilled } from 'apps/fleet-planner/components/activation/ProvideInsurancePreFilled'
import ProvideSafetyLetter from 'apps/fleet-planner/components/activation/ProvideSafetyLetter'
import { TStep } from 'apps/fleet-planner/components/activation/Steps'
import { EActivationStep } from 'apps/fleet-planner/components/activation/constants'
import { IBcaInformation, TBcaSteps } from 'apps/fleet-planner/components/activation/types'
import { FP_HOME } from 'apps/fleet-planner/config'
import { useActivationContext } from 'apps/fleet-planner/context'
import { getHighwayCarrierInfo } from '../../utils/getHighwayCarrierInfo'
import { useActivationPageHighwayQuery } from './graphql/ActivationPageHighway'
import { getDeprecatedStepCount, getStepCount } from './utils/getStepCount'

export const ActivationPage = () => {
  const { id: carrierApplicationId } = useParams<{ id: string }>()
  const {
    activationSteps,
    carrierDetails,
    currentStep,
    getMarketplaceCarrierApplication,
    insuranceContacts: contacts,
    isLoading,
    isSafetyLetterNeeded,
    mobileApp,
  } = useActivationContext()

  const { fleetPlannerMotorCarrierId = '' } = carrierDetails || {}

  const { data, loading } = useActivationPageHighwayQuery({
    variables: { motorCarrierId: fleetPlannerMotorCarrierId },
  })

  const highWayCarrierInfo = getHighwayCarrierInfo(data)
  const { insurancePolicies = [] } = highWayCarrierInfo || {}

  const [bcaPart, setBcaPart] = useState<TBcaSteps>(1)
  const [bcaInformation, setBcaInformation] = useState<IBcaInformation>({
    step1: {
      ein: '',
      entityType: '',
      address: '',
      addressLine2: '',
      city: '',
      contactEmail: '',
      contactPhone: '',
      countryCode: '',
      stateCode: '',
      zipcode: '',
    },
    step2: { signatory: '' },
  })
  const isFmsHighwayCarrierOnboardingEnabled = useFeatureFlag('fmsHighwayCarrierOnboarding')
  const isSubmitSafetyLetterEnabled = useFeatureFlag('fmsSubmitSafetyLetter')

  // on page load get the application if carrierApplicationId is present
  useEffect(() => {
    if (carrierApplicationId) {
      getMarketplaceCarrierApplication(carrierApplicationId)
    }
  }, [carrierApplicationId, getMarketplaceCarrierApplication])

  const deprecatedStepCount = getDeprecatedStepCount({
    bcaPart,
    currentStep: currentStep as TStep,
  })

  const stepCount = getStepCount({
    bcaPart,
    currentStep,
    isSafetyLetterNeeded: isSafetyLetterNeeded && isSubmitSafetyLetterEnabled,
  })

  const stepCounter = isFmsHighwayCarrierOnboardingEnabled ? stepCount : deprecatedStepCount

  const RedirectToHome = () => {
    const { hasSignedBca, hasUploadedDocuments } = activationSteps
    const completedActivation = hasSignedBca && hasUploadedDocuments
    const currentStepIsBca = currentStep === EActivationStep.bca
    const currentStepIsDocuments = currentStep === EActivationStep.documents
    const currentStepIsInsurance = currentStep === EActivationStep.insurance
    const noSafetyLetterNeeded =
      currentStep === EActivationStep.safetyLetter && !isSafetyLetterNeeded
    const noValidCurrentStep = !currentStepIsBca && !currentStepIsDocuments
    const noValidCurrentNewStep =
      !currentStepIsBca &&
      !currentStepIsDocuments &&
      !currentStepIsInsurance &&
      noSafetyLetterNeeded
    const { insuranceContacts = [] } = contacts || {}
    const completedNewActivation = insuranceContacts.length && completedActivation

    const redirectToHomeCondition1 =
      (completedActivation || noValidCurrentStep) && !isFmsHighwayCarrierOnboardingEnabled
    const redirectToHomeCondition2 =
      (completedNewActivation || noValidCurrentNewStep) && isFmsHighwayCarrierOnboardingEnabled

    if (redirectToHomeCondition1 || redirectToHomeCondition2) {
      return <Redirect to={FP_HOME} />
    }

    return null
  }

  if (loading) {
    return <Spinner />
  }

  return (
    <AppFrame isMobileApp={mobileApp}>
      <Box p={2.5}>
        <Box pt={4}>
          <Grid container alignItems='center' justifyContent='center'>
            <Grid item xs={12} md={8} lg={6}>
              {!isLoading && <Typography variant='subtitle1'>{stepCounter || '0'}</Typography>}
            </Grid>
          </Grid>
        </Box>
        <RedirectToHome />
        {currentStep === EActivationStep.bca && !isFmsHighwayCarrierOnboardingEnabled && (
          <DeprecatedBrokerCarrierAgreementForm bcaPart={bcaPart} setBcaPart={setBcaPart} />
        )}
        {currentStep === EActivationStep.bca && isFmsHighwayCarrierOnboardingEnabled && (
          <BrokerCarrierAgreementForm
            bcaPart={bcaPart}
            setBcaPart={setBcaPart}
            setBcaInformation={setBcaInformation}
            bcaInformation={bcaInformation}
          />
        )}
        {currentStep === EActivationStep.insurance &&
          isFmsHighwayCarrierOnboardingEnabled &&
          insurancePolicies.length === 0 && <ProvideInsurance />}
        {currentStep === EActivationStep.insurance &&
          isFmsHighwayCarrierOnboardingEnabled &&
          insurancePolicies.length > 0 && <ProvideInsurancePreFilled />}
        {currentStep === EActivationStep.documents && !isFmsHighwayCarrierOnboardingEnabled && (
          <DeprecatedDocumentsUpload />
        )}
        {currentStep === EActivationStep.documents && isFmsHighwayCarrierOnboardingEnabled && (
          <DocumentsUpload />
        )}
        {currentStep === EActivationStep.safetyLetter &&
          isSafetyLetterNeeded &&
          isSubmitSafetyLetterEnabled && <ProvideSafetyLetter />}
      </Box>
    </AppFrame>
  )
}

export default ActivationPage
